import { useMemo, useState } from 'react';

import {
  Autocomplete,
  Button,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { ScheduledRebalancingTimeZone } from '@cast/types';
import {
  constraintsByOption,
  cronToFormState,
  FormState,
  formStateToCron,
} from '@cast/split-me/components/schedules/ScheduleTabs';
import { useIsKentRebalancingMode } from '@cast/split-me/features/cluster/rebalance';
import { useTimeZonesQuery } from '@cast/split-me/hooks/queries/scheduled-rebalancing';

import { useAutoOptimization } from '../../../../../_hooks/useAutoOptimization';
import { MetricsGrid } from '../../MetricsGrid';
import { RebalanceOptionLayout } from '../../RebalanceOptionLayout';
import {
  formatAt,
  formatAtLabel,
  formatFrequency,
  formatTimeZoneDisplayName,
} from './_utils';
import { ScheduleFrequencyFields } from './ScheduleFrequencyFields';

type TimeZone = ScheduledRebalancingTimeZone & { displayName?: string };

type ScheduleDraft = { formState: FormState; timeZone: string };

// Kent schedules are UTC-only,
// so the timezone is displayed read-only with hardcoded value.
const ReadOnlyUtcTimeZoneInput = () => (
  <Autocomplete
    size="sm"
    inputProps={{ label: 'Time zone', readOnly: true }}
    value="UTC"
    options={['UTC']}
    endAdornment={<Typography variant="inherit">UTC+00:00</Typography>}
  />
);

export const ScheduleRebalanceSection = () => {
  const {
    rebalancingScheduleFormState,
    onToggleScheduleOption,
    onUpdateScheduleForm,
  } = useAutoOptimization();
  const isKentRebalancingMode = useIsKentRebalancingMode();

  const { timeZones: _timeZones } = useTimeZonesQuery();
  const timeZones: TimeZone[] | undefined = useMemo(
    () =>
      _timeZones?.map((tz) => ({
        ...tz,
        displayName: tz.name ? formatTimeZoneDisplayName(tz.name) : undefined,
      })),
    [_timeZones]
  );

  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft | null>(
    null
  );

  const selectedTimeZone = useMemo(
    () =>
      timeZones?.find(
        (tz) => tz.name === rebalancingScheduleFormState.timeZone
      ) ?? null,
    [timeZones, rebalancingScheduleFormState.timeZone]
  );

  const committedFormState =
    cronToFormState(rebalancingScheduleFormState.cron) ??
    constraintsByOption['--'].formState;

  const validDraftCron =
    scheduleDraft && formStateToCron(scheduleDraft.formState);

  const handleFormStateChange = (formState: FormState) =>
    setScheduleDraft((previousDraft) =>
      previousDraft ? { ...previousDraft, formState } : previousDraft
    );

  const handleSave = () => {
    if (!scheduleDraft || !validDraftCron) {
      return;
    }
    onUpdateScheduleForm({
      cron: validDraftCron,
      timeZone: scheduleDraft.timeZone,
    });
    setScheduleDraft(null);
  };

  return (
    <RebalanceOptionLayout
      title="Schedule rebalance"
      checked={rebalancingScheduleFormState.enabled}
      onChange={onToggleScheduleOption}
    >
      {scheduleDraft ? (
        <Stack
          direction="row"
          alignItems="flex-end"
          gap={8}
          sx={{
            border: '1px solid',
            borderColor: tokens.theme.border.structural.neutral,
            borderRadius: tokens.theme.radius.xs,
            backgroundColor: tokens.theme.bg.structural.surfaceRaised,
            px: 16,
            py: 12,
          }}
        >
          {isKentRebalancingMode ? (
            <ReadOnlyUtcTimeZoneInput />
          ) : (
            <Autocomplete<TimeZone, string>
              size="sm"
              inputProps={{ label: 'Time zone' }}
              options={timeZones || []}
              optionLabel="displayName"
              optionValue="name"
              value={scheduleDraft.timeZone}
              onChange={(_option, selectedTimeZoneName: string) =>
                setScheduleDraft((previousDraft) =>
                  previousDraft
                    ? { ...previousDraft, timeZone: selectedTimeZoneName }
                    : previousDraft
                )
              }
              endAdornment={(timeZone: TimeZone | null) =>
                timeZone ? (
                  <Typography variant="inherit" noWrap>
                    UTC{timeZone.offset}
                  </Typography>
                ) : null
              }
              popoverProps={{
                PaperProps: {
                  sx: {
                    width: 240,
                  },
                },
              }}
              renderOption={(timeZone: TimeZone, props) => (
                <ListItem {...props}>
                  <ListItemText
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    primary={
                      <Typography variant="inherit">
                        {timeZone.displayName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="inherit">
                        UTC{timeZone.offset}
                      </Typography>
                    }
                  />
                </ListItem>
              )}
              virtualization
            />
          )}

          <ScheduleFrequencyFields
            formState={scheduleDraft.formState}
            onChange={handleFormStateChange}
          />

          <Stack direction="row" gap={8} alignItems="center">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setScheduleDraft(null)}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="button"
              disabled={!validDraftCron}
              onClick={handleSave}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      ) : (
        <MetricsGrid
          rows={[
            [
              {
                label: 'Timezone',
                value: isKentRebalancingMode
                  ? 'UTC'
                  : selectedTimeZone?.displayName ??
                    rebalancingScheduleFormState.timeZone,
                unit: isKentRebalancingMode
                  ? 'UTC+00:00'
                  : selectedTimeZone
                  ? `UTC${selectedTimeZone.offset}`
                  : undefined,
              },
              {
                label: 'Cycle frequency',
                value: formatFrequency(committedFormState),
              },
              {
                label: formatAtLabel(committedFormState),
                value: formatAt(committedFormState),
              },
            ],
          ]}
          columnsPerRow={3}
          adornment={
            <Button
              variant="text"
              onClick={() =>
                setScheduleDraft({
                  formState: committedFormState,
                  timeZone: rebalancingScheduleFormState.timeZone,
                })
              }
            >
              Edit
            </Button>
          }
        />
      )}
    </RebalanceOptionLayout>
  );
};
